import { useEffect, useId } from 'react';

import { useUnloadPromptContext } from '@/navigation/unloadPrompt.context';

/**
 * @description This hook is used to prompt the user if they try to close the tab or reload the page.
 * Each call registers a unique subscriber, so multiple forms on the
 * same page can independently report their "should block" state
 * without racing — the global flag is OR'd across all subscribers.
 * @param shouldBlock - This boolean is used to determine if the user should be prompted.
 * If true, the user will be prompted. If false, the user will not be prompted.
 * @returns {shouldBlockNavigation, setShouldBlockNavigation} - The current
 * derived global value and the imperative setter (writes to the manual
 * override slot, which contributes to the OR alongside subscribers).
 */
export const useOnBeforeUnloadPrompt = (shouldBlock: boolean) => {
  const id = useId();
  const {
    shouldBlockNavCtx,
    setShouldBlockNavCtx,
    registerBlockSubscriber,
    unregisterBlockSubscriber,
  } = useUnloadPromptContext();

  useEffect(() => {
    registerBlockSubscriber(id, shouldBlock);
    return () => {
      unregisterBlockSubscriber(id);
    };
  }, [id, shouldBlock, registerBlockSubscriber, unregisterBlockSubscriber]);

  return {
    shouldBlockNavigation: shouldBlockNavCtx,
    setShouldBlockNavigation: setShouldBlockNavCtx,
  };
};
